import {useCallback, useMemo} from 'react';
import {useMutation} from '@sail/data';
import {useMemoizedValue} from '@sail/react';

import type {GraphQlDocument} from '@sail/data';

import {useV1Context} from '../../shared/v1Context';
import mergeContextVariables from '../../shared/utils/mergeContextVariables';
import wrapOptionsWithCache from '../utils/wrapOptionsWithCache';

import type {V1MutationOptions, V1MutationReturn} from '../types';
import type {V1ContextInput} from '../../../schema/types';

export default function useV1Mutation<
  TData,
  TVariables extends {v1Context: V1ContextInput},
  TPermissions,
  THookVariables extends Partial<TVariables> = {},
>(
  query: GraphQlDocument<TData, TVariables, TPermissions>,
  options: V1MutationOptions<
    GraphQlDocument<TData, TVariables, TPermissions>,
    THookVariables
  >,
): V1MutationReturn<
  GraphQlDocument<TData, TVariables, TPermissions>,
  THookVariables
> {
  const v1Context = useV1Context();

  const v1ContextFromHook = useMemoizedValue(
    mergeContextVariables(v1Context, options?.variables?.v1Context),
  );

  const [callback, result] = useMutation<
    TData,
    TVariables,
    TPermissions,
    THookVariables
  >(query, wrapOptionsWithCache(options, v1ContextFromHook));

  const callbackWrapped = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (...restArgs: any[]) => {
      const cbOptions = restArgs[0];

      return callback(
        Object.assign({}, cbOptions, {
          variables: Object.assign({}, cbOptions?.variables, {
            v1Context: mergeContextVariables(
              v1ContextFromHook,
              cbOptions?.variables?.v1Context,
            ),
          }),
        }),
      );
    },
    [callback, v1ContextFromHook],
  );

  return useMemo(() => [callbackWrapped, result], [callbackWrapped, result]);
}
